import {
  Avatar,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  additionalInfoImageWrapperStyle,
  additionalInfoImageContainerStyle,
  orderDetailsContainerStyle,
  subContainerHeadingStyle,
  subContainerStyle,
  additionalInfoImageStyle,
  additionalInfoTextStyle,
  visitPriceBoxStyle,
  digitalProductTagStyle,
} from "../styles/styles";
import DigitalDeliveryNotice from "./DigitalDeliveryNotice";
import { useState } from "react";
import RequestServiceAccordian from "../../../Components/RequestServiceAccordian";
import { useTranslation } from "react-i18next";
import { formatPrice } from "../../../Helpers/formating";
import { getCurrencySymbol } from "../../../Utils/CountriesData/getCountriesData";
import { editOrderLinkStyle } from "./EditOrder/styles";

// Products-table cell styles — same as the "Ordered Products" table in
// ProductOrderDetails (Figma: Product name | Qty | Product ID | Price).
const productHeaderCellStyle = {
  fontSize: "13px",
  fontWeight: 500,
  color: "rgba(62, 63, 76, 0.6)",
  borderBottom: "1px solid rgba(212, 212, 212, 0.5)",
  padding: "10px 12px",
};
const productBodyCellStyle = {
  fontSize: "14px",
  color: "rgba(62, 63, 76, 0.9)",
  borderBottom: "1px solid rgba(212, 212, 212, 0.5)",
  padding: "8px 12px",
};

const OrderDetails = (props) => {
  const { jobData, viewType, onEditOrder } = props;
  const { requested_services, additional_information, order_summary } = jobData;
  // Products attached to this service order (COMY1-292) — rendered as rows
  // tagged "(product)" under the services list, per Figma.
  const products = jobData.products || [];
  const visit_price = order_summary?.visit_price;
  // Immutable [VISIT_ONLY:] marker — stays true even after the supplier adds
  // services/products, so the visit price keeps showing (COMY1-292).
  const isVisitOrder = order_summary?.is_visit_order;
  const currencySymbol = getCurrencySymbol(order_summary?.currency);
  // Digital delivery (COMY1-334): a product line the supplier flagged as
  // digital, which they have to send to the client directly. An online service
  // is NOT digital delivery — the supplier performs it over a meeting link.
  const hasDigitalItems = products.some((p) => p.is_digital);
  const { t } = useTranslation("view-job-modal");
  const { t: tTable } = useTranslation("page-my-jobs", {
    keyPrefix: "table_data",
  });

  // Strip internal system tags (e.g. "[VISIT_ONLY:160]", "[CATEGORY:42]") from description
  const cleanDescription = additional_information?.description
    ? additional_information.description.replace(/\[(VISIT_ONLY|CATEGORY):\d+\]\s*/g, "").trim()
    : null;
  const hasDescription = !!cleanDescription;
  const [expandedAccordion, setExpandedAccordion] = useState(null);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedAccordion(isExpanded ? panel : null);
  };

  // Show the visit view for a genuine visit order (immutable [VISIT_ONLY]
  // marker) OR any order with nothing on it yet — no services AND no products —
  // which is a pure visit situation, even before a visit price is set. A
  // product-only order (has products, no services) is NOT a visit and shows its
  // products instead. COMY1-292.
  const showVisitOnly =
    isVisitOrder ||
    (requested_services.length === 0 && products.length === 0);
  const showServices =
    !showVisitOnly && (requested_services.length > 0 || products.length > 0);
  // Product-only order (no services): the section header itself carries the
  // "Products" title (top spot, like "Services & variants"), so the table below
  // drops its own heading and the extra gap. COMY1-292.
  const productOnly = showServices && requested_services.length === 0;

  // Products table (COMY1-292): the Figma products table — Product name | Qty |
  // Product ID | Price. Shown under the services list AND under a visit-only
  // order (materials added during the visit). Mirrors ProductOrderDetails.
  const productsTable = products.length > 0 && (
    // Extra top margin only when services sit above the table; a product-only
    // order relies on the section's own 18px gap so the title isn't far off.
    <Box sx={{ marginTop: requested_services.length > 0 ? "16px" : 0 }}>
      {!productOnly && (
        <Typography sx={{ ...subContainerHeadingStyle, mb: "10px" }}>
          {t("products_heading")}
        </Typography>
      )}
      <Box
        sx={{
          border: "1px solid rgba(212, 212, 212, 0.5)",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        <Table
          size="small"
          sx={{
            "& td:not(:first-of-type), & th:not(:first-of-type)": {
              borderInlineStart: "1px solid rgba(212, 212, 212, 0.5)",
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={productHeaderCellStyle}>
                {tTable("product_name")}
              </TableCell>
              <TableCell sx={productHeaderCellStyle}>{tTable("qty")}</TableCell>
              <TableCell sx={productHeaderCellStyle}>
                {tTable("product_id")}
              </TableCell>
              <TableCell sx={productHeaderCellStyle}>
                {t("request_service_accordian.price")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {products.map((p) => (
              <TableRow key={`product-${p.product_id}`}>
                <TableCell sx={productBodyCellStyle}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <Avatar
                      src={p.main_image_url || ""}
                      alt={p.name}
                      variant="rounded"
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: "rgba(212, 212, 212, 1)",
                      }}
                    />
                    <Typography sx={{ fontSize: "14px" }}>
                      {p.name}
                      {p.is_digital && (
                        <Box component="span" sx={digitalProductTagStyle}>
                          {t("digital_product")}
                        </Box>
                      )}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell sx={productBodyCellStyle}>{p.quantity}</TableCell>
                <TableCell sx={productBodyCellStyle}>{p.product_id}</TableCell>
                <TableCell sx={productBodyCellStyle}>
                  {/* Line total (qty × unit price). */}
                  {currencySymbol}
                  {formatPrice(p.subtotal)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );

  return (
    <Box className="hide-scrollbar" sx={orderDetailsContainerStyle}>
      {showVisitOnly ? (
        <Box sx={subContainerStyle}>
          {/* A visit-only order is still editable (COMY1-292): the supplier can
              add the services/products/materials used during the visit. */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap="12px"
          >
            <Typography sx={subContainerHeadingStyle}>
              {t("requested_a_visit")}
            </Typography>
            {onEditOrder && (
              <Button
                variant="text"
                onClick={onEditOrder}
                sx={editOrderLinkStyle}
                disableRipple
              >
                {t("edit.edit_order")}
              </Button>
            )}
          </Box>

          <Box sx={visitPriceBoxStyle}>
            <Typography>
              {t("Visit Price")}: {visit_price ? `${currencySymbol}${formatPrice(visit_price)}` : "-"}
            </Typography>
          </Box>

          {/* Services the supplier added during the visit (COMY1-292). */}
          {requested_services.length > 0 && (
            <Box>
              <Typography sx={{ ...subContainerHeadingStyle, mb: "10px" }}>
                {t("services_and_variants")}
              </Typography>
              {requested_services.map((service) => (
                <RequestServiceAccordian
                  key={service.id}
                  {...{
                    service,
                    expandedAccordion,
                    handleAccordionChange,
                  }}
                />
              ))}
            </Box>
          )}

          {/* Materials/products added to a visit-only order (COMY1-292). */}
          {productsTable}
        </Box>
      ) : showServices ? (
        <Box sx={subContainerStyle}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap="12px"
          >
            {requested_services.length > 0 ? (
              <Typography sx={subContainerHeadingStyle}>
                {t("services_and_variants")}
              </Typography>
            ) : (
              // Product-only order: "Products" takes the section-title spot so it
              // aligns with the top (like "Services & variants"); the table below
              // drops its own heading to avoid a duplicate. COMY1-292.
              <Typography sx={subContainerHeadingStyle}>
                {t("products_heading")}
              </Typography>
            )}
            {onEditOrder && (
              <Button
                variant="text"
                onClick={onEditOrder}
                sx={editOrderLinkStyle}
                disableRipple
              >
                {t("edit.edit_order")}
              </Button>
            )}
          </Box>

          {/* Accordions live alone in this Box: their bottom border comes from
              the :last-child rule, so no sibling may follow them here. */}
          {requested_services.length > 0 && (
            <Box>
              {requested_services.map((service) => (
                <RequestServiceAccordian
                  key={service.id}
                  {...{
                    service,
                    expandedAccordion,
                    handleAccordionChange,
                  }}
                />
              ))}
            </Box>
          )}

          {productsTable}
        </Box>
      ) : null}

      {(additional_information?.images?.length > 0 || hasDescription) && (
        <Box
          sx={{
            ...subContainerStyle,
            display: viewType === "missed" ? "none" : "flex",
          }}
        >
          <Typography sx={subContainerHeadingStyle}>
            {showVisitOnly || showServices ? t("additional_info") : t("job_information")}
          </Typography>

          {additional_information?.images?.length > 0 && (
            <Box sx={additionalInfoImageContainerStyle}>
              {additional_information.images.map((image, idx) => {
                const url = typeof image === "string" ? image : image.url;
                const isVideo = url.includes("media=video");

                return (
                  <a
                    key={idx}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: "none" }}
                  >
                    <Box sx={{ ...additionalInfoImageWrapperStyle, cursor: "pointer", overflow: "hidden" }}>
                      {isVideo ? (
                        <video
                          src={url}
                          controls
                          style={additionalInfoImageStyle}
                        />
                      ) : (
                        <img
                          src={url}
                          alt={`image-${idx}`}
                          style={additionalInfoImageStyle}
                        />
                      )}
                    </Box>
                  </a>
                );
              })}
            </Box>
          )}

          {hasDescription && (
            <Typography sx={additionalInfoTextStyle}>
              {cleanDescription}
            </Typography>
          )}
        </Box>
      )}

      {/* Digital product line on a service order — the supplier sends it to the
          client directly (COMY1-334). Sits at the foot of the panel;
          `marginTop: auto` pushes it down on a short order so it reads as a
          closing instruction. */}
      {hasDigitalItems && viewType !== "missed" && (
        <Box sx={{ marginTop: "auto" }}>
          <DigitalDeliveryNotice />
        </Box>
      )}
    </Box>
  );
};

export default OrderDetails;
